import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ChevronUp, Clock } from 'lucide-react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import type { FeatureRequest } from '@/lib/services/feedback'
import { useTranslation } from '@/hooks/useTranslation'

interface Tag {
	id: string
	name: string
	color?: string | null
}

interface RoadmapFeature extends FeatureRequest {
	tags?: Tag[]
}

interface RoadmapFeatureCardProps {
	feature: RoadmapFeature
	onViewDetails: () => void
	isReadOnly?: boolean
}

export function RoadmapFeatureCard({
	feature,
	onViewDetails,
	isReadOnly = false,
}: RoadmapFeatureCardProps) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ 
		id: feature.id,
		disabled: isReadOnly // Disable dragging for read-only users
	})

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.3 : 1,
	}

	const handleClick = (e: React.MouseEvent) => {
		// Only trigger click if not dragging
		if (!isDragging) {
			e.stopPropagation()
			onViewDetails()
		}
	}

	const hasDescription = feature.description && feature.description.length > 0
	const truncatedDescription = hasDescription
		? feature.description!.length > 100
			? feature.description!.substring(0, 100) + '...'
			: feature.description
		: null

	return (
		<motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
			<Card
				ref={setNodeRef}
				style={style}
				{...attributes}
				{...listeners}
				onClick={handleClick}
				className={cn(
					'group shadow-sm hover:shadow-lg transition-all duration-200 bg-card border-border/50',
					'hover:border-primary/30 hover:bg-card/80',
					!isReadOnly && 'cursor-grab active:cursor-grabbing',
					isReadOnly && 'cursor-pointer',
					isDragging && 'shadow-2xl ring-2 ring-primary/20'
				)}>
				<CardContent className="p-4">
					{/* Title */}
					<h4 className="font-semibold text-sm mb-2 text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors">
						{feature.title}
					</h4>

					{/* Description */}
					{truncatedDescription && (
						<p className="text-xs text-muted-foreground mb-3 leading-relaxed line-clamp-2">
							{truncatedDescription}
						</p>
					)}

					{/* Tags */}
					{feature.tags && feature.tags.length > 0 && (
						<div className="flex flex-wrap gap-1 mb-3">
							{feature.tags.slice(0, 2).map((tag) => (
								<Badge
									key={tag.id}
									variant="secondary"
									className="text-xs px-2 py-0.5 font-normal bg-muted/60 hover:bg-muted transition-colors">
									{tag.name}
								</Badge>
							))}
							{feature.tags.length > 2 && (
								<Badge
									variant="outline"
									className="text-xs px-2 py-0.5 font-normal text-muted-foreground">
									+{feature.tags.length - 2}
								</Badge>
							)}
						</div>
					)}

					{/* Footer with metrics */}
					<div className="flex items-center justify-between pt-2 border-t border-border/30">
						<div className="flex items-center gap-3 text-xs text-muted-foreground">
							{/* Upvotes */}
							<div className="flex items-center gap-1 hover:text-primary transition-colors">
								<ChevronUp className="h-3 w-3" />
								<span className="font-medium">{feature.upvote_count || 0}</span>
							</div>
						</div>

						{/* Created date */}
						{feature.created_at && (
							<div className="flex items-center gap-1 text-xs text-muted-foreground">
								<Clock className="h-3 w-3" />
								<span>
									{new Date(feature.created_at).toLocaleDateString('en-US', {
										month: 'short',
										day: 'numeric',
									})}
								</span>
							</div>
						)}
					</div>

					{/* Drag indicator */}
					<div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
						<div className="w-4 h-4 grid grid-cols-2 gap-0.5">
							{Array.from({ length: 4 }).map((_, i) => (
								<div
									key={i}
									className="w-1 h-1 bg-muted-foreground/40 rounded-full"
								/>
							))}
						</div>
					</div>
				</CardContent>
			</Card>
		</motion.div>
	)
}
